/**
 * Tiny offline DSP toolkit for the procedural sound bank. Everything renders into Float32Arrays
 * (mono) so sounds can be generated once, tested without WebAudio, and copied into AudioBuffers.
 */

export const TAU = Math.PI * 2;

/** Deterministic noise source (xorshift32) so sounds are identical on every run. */
export class Noise {
  private s: number;
  constructor(seed = 0x9e3779b9) {
    this.s = seed >>> 0 || 1;
  }
  /** Uniform in [-1, 1). */
  next(): number {
    let x = this.s;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.s = x >>> 0;
    return this.s / 2147483648 - 1;
  }
  /** Uniform in [0, 1). */
  unit(): number {
    return (this.next() + 1) * 0.5;
  }
}

/** Exponential decay envelope with a linear attack. */
export function env(t: number, attack: number, decay: number): number {
  if (t < 0) return 0;
  if (t < attack) return t / attack;
  return Math.exp(-(t - attack) / decay);
}

/** Attack / hold / release envelope for sustained sounds. */
export function ahr(t: number, attack: number, hold: number, release: number): number {
  if (t < 0) return 0;
  if (t < attack) return t / attack;
  if (t < attack + hold) return 1;
  return Math.max(0, 1 - (t - attack - hold) / release);
}

/**
 * One-pole low-pass. The cutoff may change per sample; the coefficient is recomputed only when
 * the cutoff moves by more than ~1% (control rate), which keeps sweeps cheap.
 */
export class LP1 {
  y = 0;
  private a = 1;
  private lastCut = -1;
  constructor(private sr: number) {}
  run(x: number, cutoff: number): number {
    if (Math.abs(cutoff - this.lastCut) > this.lastCut * 0.01) {
      this.lastCut = cutoff;
      this.a = 1 - Math.exp((-TAU * Math.min(cutoff, this.sr * 0.45)) / this.sr);
    }
    this.y += (x - this.y) * this.a;
    return this.y;
  }
}

/** One-pole high-pass. */
export class HP1 {
  private lp: LP1;
  constructor(sr: number) {
    this.lp = new LP1(sr);
  }
  run(x: number, cutoff: number): number {
    return x - this.lp.run(x, cutoff);
  }
}

/** RBJ biquad (band-pass / low-pass / high-pass / peak), coefficients updatable per sample. */
export class Biquad {
  private x1 = 0;
  private x2 = 0;
  private y1 = 0;
  private y2 = 0;
  private b0 = 1;
  private b1 = 0;
  private b2 = 0;
  private a1 = 0;
  private a2 = 0;
  private lastF = -1;
  private lastQ = -1;
  constructor(
    private sr: number,
    private type: 'bp' | 'lp' | 'hp',
  ) {}
  set(f: number, q: number): void {
    // Control rate: skip tiny frequency changes (sweeps update every ~1%).
    if (q === this.lastQ && Math.abs(f - this.lastF) <= this.lastF * 0.01) return;
    this.lastF = f;
    this.lastQ = q;
    const w = (TAU * Math.min(f, this.sr * 0.45)) / this.sr;
    const cw = Math.cos(w);
    const alpha = Math.sin(w) / (2 * q);
    const a0 = 1 + alpha;
    let b0: number;
    let b1: number;
    let b2: number;
    if (this.type === 'bp') {
      b0 = alpha;
      b1 = 0;
      b2 = -alpha;
    } else if (this.type === 'lp') {
      b0 = (1 - cw) / 2;
      b1 = 1 - cw;
      b2 = (1 - cw) / 2;
    } else {
      b0 = (1 + cw) / 2;
      b1 = -(1 + cw);
      b2 = (1 + cw) / 2;
    }
    this.b0 = b0 / a0;
    this.b1 = b1 / a0;
    this.b2 = b2 / a0;
    this.a1 = (-2 * cw) / a0;
    this.a2 = (1 - alpha) / a0;
  }
  run(x: number, f?: number, q?: number): number {
    if (f !== undefined) this.set(f, q ?? this.lastQ);
    const y = this.b0 * x + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
    this.x2 = this.x1;
    this.x1 = x;
    this.y2 = this.y1;
    this.y1 = y;
    return y;
  }
}

/** Phase-accumulating oscillator (supports frequency sweeps without clicks). */
export class Osc {
  phase = 0;
  constructor(private sr: number) {}
  step(freq: number): number {
    this.phase += freq / this.sr;
    this.phase -= Math.floor(this.phase);
    return this.phase;
  }
  sine(freq: number): number {
    return Math.sin(TAU * this.step(freq));
  }
  square(freq: number, duty = 0.5): number {
    return this.step(freq) < duty ? 1 : -1;
  }
  saw(freq: number): number {
    return this.step(freq) * 2 - 1;
  }
  tri(freq: number): number {
    const p = this.step(freq);
    return p < 0.5 ? p * 4 - 1 : 3 - p * 4;
  }
}

/** Renders `dur` seconds of a per-sample generator. */
export function render(dur: number, sr: number, fn: (t: number, i: number) => number): Float32Array {
  const n = Math.max(1, Math.floor(dur * sr));
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = fn(i / sr, i);
  return out;
}

/** Mixes b into a starting at offset seconds (in place on a; a must be long enough). */
export function mixInto(a: Float32Array, b: Float32Array, offset: number, sr: number, gain = 1): Float32Array {
  const o = Math.floor(offset * sr);
  const n = Math.min(b.length, a.length - o);
  for (let i = 0; i < n; i++) a[o + i] += b[i] * gain;
  return a;
}

/** Multi-tap echo (slapback / room) in place. taps: [delay seconds, gain]. */
export function echo(buf: Float32Array, sr: number, taps: [number, number][], damp = 0.35): Float32Array {
  const src = buf.slice();
  for (const [d, g] of taps) {
    const off = Math.floor(d * sr);
    let lp = 0;
    for (let i = off; i < buf.length; i++) {
      lp += (src[i - off] - lp) * (1 - damp);
      buf[i] += lp * g;
    }
  }
  return buf;
}

/** Simple feedback comb reverb tail (a few combs + damping), mixed in place. */
export function reverb(buf: Float32Array, sr: number, mix: number, size = 1, damp = 0.4): Float32Array {
  const src = buf.slice();
  const combs = [0.0297, 0.0371, 0.0411, 0.0437].map((d) => Math.floor(d * size * sr));
  const fb = 0.78;
  const acc = new Float32Array(buf.length);
  for (const len of combs) {
    const line = new Float32Array(len);
    let idx = 0;
    let lp = 0;
    for (let i = 0; i < buf.length; i++) {
      const out = line[idx];
      lp += (out - lp) * (1 - damp);
      line[idx] = src[i] + lp * fb;
      idx = (idx + 1) % len;
      acc[i] += out;
    }
  }
  for (let i = 0; i < buf.length; i++) buf[i] += (acc[i] / combs.length) * mix;
  return buf;
}

/** Low-passes a buffer in place (one pole, two passes for a 12 dB slope). */
export function lowpass(buf: Float32Array, sr: number, cutoff: number): Float32Array {
  for (let pass = 0; pass < 2; pass++) {
    const f = new LP1(sr);
    for (let i = 0; i < buf.length; i++) buf[i] = f.run(buf[i], cutoff);
  }
  return buf;
}

export function highpass(buf: Float32Array, sr: number, cutoff: number): Float32Array {
  const f = new HP1(sr);
  for (let i = 0; i < buf.length; i++) buf[i] = f.run(buf[i], cutoff);
  return buf;
}

/** Soft saturation for punch. */
export function drive(buf: Float32Array, amount: number): Float32Array {
  const k = Math.max(0.001, amount);
  const norm = Math.tanh(k);
  for (let i = 0; i < buf.length; i++) buf[i] = Math.tanh(buf[i] * k) / norm;
  return buf;
}

/** Scales to a target peak and applies short fades so loops/one-shots never click. */
export function finish(buf: Float32Array, sr: number, peak = 0.9, fadeIn = 0.001, fadeOut = 0.01): Float32Array {
  let p = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = buf[i];
    if (!Number.isFinite(v)) buf[i] = 0;
    else if (Math.abs(v) > p) p = Math.abs(v);
  }
  const g = p > 1e-9 ? peak / p : 0;
  const fi = Math.max(1, Math.floor(fadeIn * sr));
  const fo = Math.max(1, Math.floor(fadeOut * sr));
  for (let i = 0; i < buf.length; i++) {
    let v = buf[i] * g;
    if (i < fi) v *= i / fi;
    const r = buf.length - 1 - i;
    if (r < fo) v *= r / fo;
    buf[i] = v;
  }
  return buf;
}

/** Makes a buffer loop seamlessly by cross-fading its tail into its head. */
export function loopify(buf: Float32Array, sr: number, xfade = 0.08): Float32Array {
  const n = Math.min(Math.floor(xfade * sr), Math.floor(buf.length / 3));
  const out = buf.slice(0, buf.length - n);
  for (let i = 0; i < n; i++) {
    const t = i / n;
    out[i] = buf[i] * t + buf[buf.length - n + i] * (1 - t);
  }
  return out;
}

/** Karplus-Strong plucked string (twangs, harps, sitar-ish plucks). */
export function pluck(freq: number, dur: number, sr: number, noise: Noise, bright = 0.5, decay = 0.996): Float32Array {
  const period = Math.max(2, Math.round(sr / freq));
  const line = new Float32Array(period);
  for (let i = 0; i < period; i++) line[i] = noise.next();
  const out = new Float32Array(Math.floor(dur * sr));
  let idx = 0;
  for (let i = 0; i < out.length; i++) {
    const a = line[idx];
    const b = line[(idx + 1) % period];
    out[i] = a;
    // Averaging filter; `bright` keeps more of the raw sample (slower high-frequency decay).
    line[idx] = decay * (bright * a + (1 - bright) * 0.5 * (a + b));
    idx = (idx + 1) % period;
  }
  return out;
}
